import { forwardRef, useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { cn } from "../utils/cn";

export interface ChipListFieldProps {
  /** Current chip values (controlled). */
  values: string[];
  onChange: (values: string[]) => void;
  /** Placeholder for the inline "add" input. */
  placeholder?: string;
  /** Accessible label for the add input. */
  addLabel?: string;
  /** Hint the on-screen keyboard / validation for numeric chips. */
  inputMode?: "text" | "numeric" | "decimal";
  className?: string;
}

/**
 * A variable-length list of short values rendered as removable chips plus an
 * inline input. Enter (or blur) commits the draft; Backspace on an empty input
 * removes the last chip; duplicates are ignored. Reusable for anomalous values
 * and cut-off lists.
 */
export const ChipListField = forwardRef<HTMLDivElement, ChipListFieldProps>(
  (
    {
      values,
      onChange,
      placeholder = "Agregar…",
      addLabel = "Agregar valor",
      inputMode = "text",
      className,
    },
    ref,
  ) => {
    const [draft, setDraft] = useState("");

    const commit = () => {
      const v = draft.trim();
      setDraft("");
      if (!v || values.includes(v)) return;
      onChange([...values, v]);
    };
    const removeAt = (i: number) => onChange(values.filter((_, idx) => idx !== i));

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
      } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
        removeAt(values.length - 1);
      }
    };

    return (
      <div ref={ref} className={cn("flex flex-wrap items-center gap-2", className)}>
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-sm tabular-nums"
          >
            {v}
            <button
              type="button"
              aria-label={`Quitar ${v}`}
              onClick={() => removeAt(i)}
              className="rounded text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          inputMode={inputMode}
          placeholder={placeholder}
          aria-label={addLabel}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          className="min-w-[6rem] flex-1 rounded-md border border-dashed border-border bg-transparent px-2 py-1 text-sm outline-none focus-visible:border-primary"
        />
      </div>
    );
  },
);
ChipListField.displayName = "ChipListField";
